import jsPDF from 'jspdf';
import type {
  ClassPerfOverviewResponse, ClassPerfBySubjectRow,
  ClassPerfExamRow, ClassPerfLeaderboardResponse, ClassPerfTrendsResponse,
} from '../../../api/types';

export interface PdfSnapshot {
  schoolName: string;
  gradeName: string;
  sectionName?: string;
  subjectName?: string;
  generatedAt: string;
  overview: ClassPerfOverviewResponse;
  chartDataUri?: string;
}

export function buildOverviewPdf(s: PdfSnapshot): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  doc.setFontSize(18);
  doc.text('Class Performance — Overview', 40, 60);

  doc.setFontSize(11);
  doc.text(`${s.schoolName} · ${s.gradeName}${s.sectionName ? ' ' + s.sectionName : ''}`, 40, 84);
  if (s.subjectName) doc.text(`Subject: ${s.subjectName}`, 40, 100);
  doc.text(`Generated: ${s.generatedAt}`, 40, 116);

  let y = 152;
  const kpis: Array<[string, string]> = [
    ['Cohort',          String(s.overview.cohort_size)],
    ['Students w/data', String(s.overview.students_with_data)],
    ['Attempts',        String(s.overview.attempts_count)],
    ['Avg',             s.overview.avg_score    == null ? '—' : `${s.overview.avg_score.toFixed(1)}%`],
    ['Median',          s.overview.median_score == null ? '—' : `${s.overview.median_score.toFixed(1)}%`],
    ['P25',             s.overview.p25          == null ? '—' : `${s.overview.p25.toFixed(1)}%`],
    ['P75',             s.overview.p75          == null ? '—' : `${s.overview.p75.toFixed(1)}%`],
  ];
  for (const [k, v] of kpis) {
    doc.text(`${k}:`, 40, y);
    doc.text(v, 200, y);
    y += 18;
  }

  if (s.chartDataUri) {
    doc.addPage();
    doc.text('Score distribution', 40, 60);
    doc.addImage(s.chartDataUri, 'PNG', 40, 80, 500, 280);
  }

  return doc;
}

export interface ClassPerfPdfData {
  schoolName: string;
  gradeName: string;
  sectionName?: string;
  subjectName?: string;
  generatedAt: string;
  overview?: ClassPerfOverviewResponse;
  subjects?: ClassPerfBySubjectRow[];
  exams?: ClassPerfExamRow[];
  leaderboard?: ClassPerfLeaderboardResponse;
  trends?: ClassPerfTrendsResponse;
}

function sectionHeader(doc: jsPDF, title: string): number {
  doc.addPage();
  doc.setFontSize(15);
  doc.text(title, 40, 60);
  doc.setFontSize(10);
  return 92;
}

function rows(doc: jsPDF, header: string[], data: Array<Array<string>>, startY: number): void {
  let y = startY;
  doc.setFont('helvetica', 'bold');
  header.forEach((h, i) => doc.text(h, 40 + i * 100, y));
  doc.setFont('helvetica', 'normal');
  y += 16;
  for (const r of data) {
    if (y > 780) { doc.addPage(); y = 60; }
    r.forEach((cell, i) => doc.text(String(cell).slice(0, 18), 40 + i * 100, y));
    y += 14;
  }
}

export function buildClassPerformancePdf(d: ClassPerfPdfData): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  // Title page — spec 2.5-B §4.3: school / grade / section / subject / date
  doc.setFontSize(20);
  doc.text('Class Performance Report', 40, 80);
  doc.setFontSize(12);
  doc.text(d.schoolName || '—', 40, 116);
  doc.text(`${d.gradeName}${d.sectionName ? ' · ' + d.sectionName : ''}${d.subjectName ? ' · ' + d.subjectName : ''}`, 40, 136);
  doc.text(`Generated: ${d.generatedAt}`, 40, 156);

  if (d.overview) {
    const y = sectionHeader(doc, 'Overview');
    const o = d.overview;
    const kpis: Array<[string, string]> = [
      ['Cohort', String(o.cohort_size)],
      ['Students w/data', String(o.students_with_data)],
      ['Attempts', String(o.attempts_count)],
      ['Avg', o.avg_score == null ? '—' : `${o.avg_score.toFixed(1)}%`],
      ['Median', o.median_score == null ? '—' : `${o.median_score.toFixed(1)}%`],
      ['P25', o.p25 == null ? '—' : `${o.p25.toFixed(1)}%`],
      ['P75', o.p75 == null ? '—' : `${o.p75.toFixed(1)}%`],
    ];
    rows(doc, ['Metric', 'Value'], kpis.map(([k, v]) => [k, v]), y);
  }
  if (d.subjects?.length) {
    const y = sectionHeader(doc, 'By subject');
    rows(doc, ['Subject', 'Avg', 'Attempts', 'Students'],
      d.subjects.map(s => [s.name, s.avg_score == null ? '—' : s.avg_score.toFixed(1), String(s.attempts_count), String(s.students_with_data)]), y);
  }
  if (d.exams?.length) {
    const y = sectionHeader(doc, 'Exams');
    rows(doc, ['Exam', 'Subject', 'Took', 'Avg', 'vs prior'],
      d.exams.map(e => [e.name, e.subject?.name ?? '', String(e.students_took), String(e.avg_score), e.delta_vs_prior_on_same_subject == null ? '—' : String(e.delta_vs_prior_on_same_subject)]), y);
  }
  if (d.leaderboard?.sections?.length) {
    const y = sectionHeader(doc, 'Leaderboard');
    rows(doc, ['Section', 'Rank', 'Avg', 'Students'],
      d.leaderboard.sections.map(s => [s.display, s.rank == null ? '—' : String(s.rank), s.avg_score == null ? '—' : s.avg_score.toFixed(1), String(s.students_with_data)]), y);
  }
  if (d.trends) {
    const y = sectionHeader(doc, `Weekly trend — ${d.trends.subject_name}`);
    rows(doc, ['Week', 'Avg', 'Attempts', 'Students'],
      d.trends.series.map(p => [p.week_start, p.avg_score == null ? '—' : p.avg_score.toFixed(1), String(p.attempts_count), String(p.students_with_data)]), y);
  }
  return doc;
}

export function downloadPdf(filename: string, doc: jsPDF): void {
  doc.save(filename);
}
